import { useFetch as useNuxtFetch, useNuxtApp } from "#app";

function isProtectedApiRequest(request: unknown) {
  return typeof request === "string" && request.startsWith("/api/");
}

/**
 * Wrapper around Nuxt's useFetch that auto-injects API access token for protected routes.
 * Disables SSR for /api/* requests to prevent unauthenticated server calls.
 * Use this for all /api/* calls instead of native useFetch.
 */
export const useApiFetch = <T,>(
  url: string | (() => string),
  options: any = {},
) => {
  const nuxtApp = useNuxtApp();
  const shouldDisableServer =
    typeof url === "string" && isProtectedApiRequest(url) && options.server === undefined;

  const originalOnRequest = options.onRequest;

  const fetchOptions = {
    ...options,
    server: shouldDisableServer ? false : options.server,
    async onRequest(ctx: any) {
      if (import.meta.client && isProtectedApiRequest(ctx.request)) {
        const token = await nuxtApp.$getAccessToken?.();
        if (!token) {
          throw new Error("No API access token available");
        }

        const headers = new Headers(ctx.options.headers || {});
        headers.set("Authorization", `Bearer ${token}`);
        ctx.options.headers = headers;
      }

      if (originalOnRequest) {
        await originalOnRequest(ctx);
      }
    },
  };

  return useNuxtFetch<T>(url, fetchOptions);
};
